/**
 * CustomFontFamily Plugin for CKEditor
 *
 * This plugin allows users to customize the font family of selected text within CKEditor. It integrates two core components:
 * - **CustomFontFamilyEditing**: Manages the logic for editing font family.
 * - **CustomFontFamilyUI**: Handles the user interface for selecting and applying font families.
 *
 * ## Features:
 * - **Font Family Editing**:
 *   - Provides functionality for changing the font family of the selected text in the editor.
 *   - Integrates with CKEditor's command system to apply font family changes.
 * - **UI for Font Family Selection**:
 *   - Adds a dropdown or button UI for the user to choose and apply font families.
 * - **Official Plugin**:
 *   - This plugin is designed as an official CKEditor plugin for easy integration into CKEditor-based applications.
 *
 * ## Usage:
 * The plugin enables users to change the font family of text. It can be used via the UI (a dropdown or button), and integrates seamlessly with CKEditor commands.
 * Example UI integration:
 * ```javascript
 * editor.ui.componentFactory.add(CKPlugins.CustomFontFamily, locale => {
 *     // Create dropdown or UI for font family selection
 * });
 * ```
 *
 * ## Configuration:
 * You can configure the available font families by setting the `options` property in the plugin's configuration.
 * Example:
 * ```javascript
 * editor.config.set(CKPlugins.CustomFontFamily, {
 *     fontFamilyOptions: [...] // List of available font families
 * });
 * ```
 *
 * ## Implementation Details:
 * - **Font Family Editing**: The `CustomFontFamilyEditing` class handles the application of the font family to selected text.
 * - **UI Integration**: The `CustomFontFamilyUI` class manages the dropdowns or button views for the user to select and apply font families.
 * - **CKEditor Command Integration**: The plugin uses CKEditor commands to execute font family changes based on the user's selection.
 *
 * ## References:
 * - CKEditor Plugin API: https://ckeditor.com/docs/ckeditor5/latest/api/module_core_plugin-Plugin.html
 * - CKEditor UI Components: https://ckeditor.com/docs/ckeditor5/latest/api/module_ui_componentfactory-ComponentFactory.html
 *
 * @module CustomFontFamily
 * @exports CustomFontFamily
 */

import CustomFontFamilyEditing from './adapters/fontFamilyEditing';
import CustomFontFamilyUI from './adapters/fontFamilyUI';

const Plugin = window.ClassicEditor.Plugin;

export default class CustomFontFamily extends Plugin {
    static get pluginName() {
        return 'CustomFontFamily';
    }

    static get requires() {
        return [CustomFontFamilyEditing, CustomFontFamilyUI];
    }

    static get isOfficialPlugin() {
        return true;
    }
}
